/**
 * AURA State Manager
 * Centralized store pattern with subscribers
 */

import {
  AuraState,
  AuraSettings,
  PartialAuraSettings,
  ProfileName,
  createDefaultState,
  createDefaultSettings,
} from './types';
import { eventBus } from './events';
import { Logger } from './logger';

export { createDefaultState } from './types';

type StateSubscriber = (state: AuraState) => void;

export class StateManager {
  private state: AuraState;
  private subscribers: Set<StateSubscriber> = new Set();
  private previousSettings: AuraSettings | null = null;
  private logger = new Logger('StateManager');

  constructor() {
    this.state = createDefaultState();
  }

  /**
   * Get current state (readonly copy)
   */
  getState(): Readonly<AuraState> {
    return { ...this.state };
  }

  /**
   * Get current settings (readonly copy)
   */
  getSettings(): Readonly<AuraSettings> {
    return JSON.parse(JSON.stringify(this.state.settings));
  }

  /**
   * Subscribe to state changes
   */
  subscribe(callback: StateSubscriber): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Update state partially
   */
  update(partial: Partial<AuraState>): void {
    this.state = { ...this.state, ...partial };
    this.notifySubscribers();
    this.logger.debug('State updated', {
      changed: Object.keys(partial),
    });
  }

  /**
   * Notify all subscribers of state change
   */
  private notifySubscribers(): void {
    const stateCopy = this.getState();
    this.subscribers.forEach((callback) => {
      try {
        callback(stateCopy);
      } catch (error) {
        this.logger.error('Error in state subscriber', error);
      }
    });
  }

  /**
   * Update settings and emit change event
   */
  updateSettings(settings: PartialAuraSettings): void {
    const newSettings: AuraSettings = {
      content: { ...this.state.settings.content, ...settings.content },
      color: { ...this.state.settings.color, ...settings.color },
      orientation: { ...this.state.settings.orientation, ...settings.orientation },
    };

    this.update({ settings: newSettings });
    eventBus.emit('settings:change', newSettings);
  }

  /**
   * Activate a profile
   */
  activateProfile(profileName: ProfileName, profileSettings: PartialAuraSettings): void {
    // Store current settings to restore later
    this.previousSettings = JSON.parse(JSON.stringify(this.state.settings));

    // Apply profile settings
    const newSettings: AuraSettings = {
      content: { ...this.state.settings.content, ...profileSettings.content },
      color: { ...this.state.settings.color, ...profileSettings.color },
      orientation: { ...this.state.settings.orientation, ...profileSettings.orientation },
    };

    this.update({
      activeProfile: profileName,
      settings: newSettings,
    });

    eventBus.emit('profile:activate', { profileName, settings: newSettings });
    eventBus.emit('settings:change', newSettings);
    this.logger.info(`Activated profile: ${profileName}`);
  }

  /**
   * Deactivate the currently active profile.
   *
   * Callers that know which keys the profile owns (i.e. the profiles module)
   * should compute the post-deactivation settings explicitly and pass them in
   * via `restoredSettings` — this guarantees "turning off a profile undoes
   * its effects" regardless of what was on before activation.  Callers that
   * don't (the public API path) fall back to the previously-captured
   * previousSettings, or to defaults if none were captured (the
   * loadFromJson reload scenario).
   */
  deactivateProfile(restoredSettings?: AuraSettings): void {
    if (!this.state.activeProfile) return;

    const previousProfile = this.state.activeProfile;
    const newSettings =
      restoredSettings ?? this.previousSettings ?? createDefaultSettings();

    this.update({
      activeProfile: null,
      settings: newSettings,
    });
    eventBus.emit('settings:change', newSettings);

    this.previousSettings = null;
    eventBus.emit('profile:deactivate', { profileName: previousProfile });
    this.logger.info(`Deactivated profile: ${previousProfile}`);
  }

  /**
   * Set panel open state
   */
  setPanelOpen(isOpen: boolean): void {
    if (this.state.isOpen === isOpen) return;
    this.update({ isOpen });
    eventBus.emit(isOpen ? 'panel:open' : 'panel:close');
  }

  /**
   * Set hidden state (hides entire widget including FAB)
   */
  setHidden(isHidden: boolean): void {
    if (this.state.isHidden === isHidden) return;
    if (isHidden) {
      this.setPanelOpen(false);
    }
    this.update({ isHidden });
    this.logger.info(`Widget ${isHidden ? 'hidden' : 'shown'}`);
  }

  /**
   * Set authentication state
   */
  setAuthenticated(isAuthenticated: boolean): void {
    this.update({ isAuthenticated });
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    this.update({ isLoading });
  }

  /**
   * Reset all accessibility settings and active profile to defaults,
   * preserving transient UI state (panel open, hidden, auth).
   */
  reset(): void {
    const previousProfile = this.state.activeProfile;
    const defaultSettings = createDefaultSettings();

    this.state = {
      ...createDefaultState(),
      isOpen: this.state.isOpen,
      isHidden: this.state.isHidden,
      isAuthenticated: this.state.isAuthenticated,
    };
    this.previousSettings = null;

    this.notifySubscribers();
    eventBus.emit('settings:change', defaultSettings);

    if (previousProfile) {
      eventBus.emit('profile:deactivate', { profileName: previousProfile });
    }

    this.logger.info('State reset to defaults');
  }

  /**
   * Load state from serialized JSON
   */
  loadFromJson(json: string): boolean {
    try {
      const parsed = JSON.parse(json);
      if (parsed.settings) {
        this.update({
          settings: parsed.settings,
          activeProfile: parsed.activeProfile ?? null,
        });
        return true;
      }
    } catch (error) {
      this.logger.error('Failed to load state from JSON', error);
    }
    return false;
  }

  /**
   * Serialize state to JSON
   */
  toJson(): string {
    return JSON.stringify({
      settings: this.state.settings,
      activeProfile: this.state.activeProfile,
    });
  }
}

// Singleton instance
export const stateManager = new StateManager();
